import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { useDebouncedCallback } from 'use-debounce';
import loadable from '@loadable/component';
import { isNil } from 'lodash';

import ErrorBoundary from 'components/ErrorBoundary';
import TimeframeValue from 'components/TimeframeValue';
import { useBreakpoints } from 'helpers/styles';
import { formatNumber } from 'helpers/common';

import s from './styled';

const ActivityChart = loadable(() =>
  import(/* webpackChunkName: "ActivityChart" */ 'components/ActivityChart'),
);

const ActivityPanel = memo(function ActivityPanel(props) {
  const {
    children,
    title,
    icon = 'graph',
    className,
    volumeLabel = 'Volume',
    timeframe,
    color = 'secondary',
    data,
    loading,
  } = props;

  const theme = useTheme();
  const chartRef = useRef();
  const { isSmUp } = useBreakpoints();

  const [hoveredPoints, setHoveredPoints] = useState([]);

  const onHover = useDebouncedCallback(
    (event, points) => {
      if (event.type === 'mouseout' || !points?.length) {
        return;
      }
      setHoveredPoints(
        points.map((point) => ({
          index: point.index,
          label: point.label,
          value: point.value,
        })),
      );
    },
    150,
    { maxWait: 150 },
  );

  /**
   * Data fetching
   */
  useEffect(() => {
    setHoveredPoints([
      {
        index: 0,
        label: data?.labels[0],
        value: data?.volume[0],
      },
      {
        index: 0,
        label: data?.labels[0],
        value: data?.spent[0],
      },
    ]);
  }, [data]);

  const underTitleNode = (
    <s.UnderTitle $noMarginLeft={!isSmUp}>
      <TimeframeValue timeframe={timeframe} />
      <s.ActiveChartLabel $color={color}>
        {(!loading && hoveredPoints[0]?.label) || (
          <Skeleton variant="text" width={50} />
        )}
      </s.ActiveChartLabel>
    </s.UnderTitle>
  );

  return (
    <ErrorBoundary>
      <s.ActivityPanel
        title={
          <div>
            {title}
            {isSmUp && underTitleNode}
          </div>
        }
        topBetween={isSmUp ? null : underTitleNode}
        icon={icon}
        className={className}
        actions={
          <>
            {[
              {
                color: theme.palette[color].main,
                label: volumeLabel,
                value: hoveredPoints[0]?.value,
              },
              {
                color: '#B4B4B4',
                label: 'Amount Spent',
                value: hoveredPoints[1]?.value,
                valueFormatter: (v) => `$${formatNumber(v)}`,
                skeletonWidth: 75,
              },
            ].map((item) => {
              return (
                <s.LegendItem key={item.label}>
                  <s.LegendBox>
                    <s.LegendLabel>{item.label}</s.LegendLabel>
                    <s.LegendValue>
                      {(loading || isNil(item.value)) && (
                        <Skeleton
                          variant="text"
                          width={item.skeletonWidth || 50}
                        />
                      )}
                      {!loading && !isNil(item.value) && (
                        <s.LegendIconVal>
                          <s.LegendIcon $color={item.color} />
                          {!!item.valueFormatter
                            ? item.valueFormatter(item.value)
                            : item.value}
                        </s.LegendIconVal>
                      )}
                    </s.LegendValue>
                  </s.LegendBox>
                </s.LegendItem>
              );
            })}
          </>
        }
      >
        <s.ActivityPanelInner>
          <s.ActivityChart>
            {!data && <s.CircularProgress $color={color} />}
            {data && (
              <ActivityChart
                ref={chartRef}
                color={color}
                volumeLabel={volumeLabel}
                labels={data?.labels}
                volume={data?.volume}
                spent={data?.spent}
                onHover={onHover}
                annotationXIndex={hoveredPoints[0]?.index}
              />
            )}
          </s.ActivityChart>

          {children}
        </s.ActivityPanelInner>
      </s.ActivityPanel>
    </ErrorBoundary>
  );
});

ActivityPanel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.string,
  volumeLabel: PropTypes.string,
  color: PropTypes.string,
  timeframe: PropTypes.array,
  data: PropTypes.object,
  loading: PropTypes.bool,
};

export default ActivityPanel;
